import { Context as ContextAppFrame } from 'scala'
import { useCallback, useContext, useMemo, useState } from 'react'
import { Feature } from 'player'
import { GetSettings } from '../../player/use-persisted-channel-settings'
import { useStorePlayer } from '../../../modules/player/player-module/store'
import { UseModalPaywall } from '../use-modal-paywall'

interface UseEventPaywall {
  dispatchEventPaywall(): void
}

interface Props {
  isDemo?: boolean
  onGetChannelSettings: GetSettings
  onOpenModalPremium: UseModalPaywall['onOpen']
}

const eventMap: { [key: string]: Feature } = {
  showLyrics: 'lyrics-lock',
  showChords: 'chords-lock',
  showSections: 'sections-lock',
  showMetronome: 'metronome-limited'
}

export const useEventPaywall = ({
  isDemo,
  onOpenModalPremium,
  onGetChannelSettings
}: Props): UseEventPaywall => {
  const { data } = useStorePlayer()
  const { user } = useContext(ContextAppFrame)

  const [showedPaywall, setShowedPaywall] = useState(false)

  const isPremium = useMemo(
    () => user?.subscription?.isPremium,
    [user?.subscription]
  )

  const hasLyrics = useMemo(
    () => data?.lyrics?.data?.length > 0,
    [data?.lyrics]
  )

  const hasSections = useMemo(
    () => data?.sections?.data?.length > 0,
    [data?.sections]
  )

  const dispatchEventPaywall = useCallback(() => {
    if (isPremium || showedPaywall || isDemo) return

    const storage = onGetChannelSettings()
    const lastFeatureInteracted = storage?.general?.lastFeatureInteracted

    if (
      (lastFeatureInteracted === 'showLyrics' && !hasLyrics) ||
      (lastFeatureInteracted === 'showSections' && !hasSections)
    ) {
      return
    }

    if (lastFeatureInteracted && eventMap[lastFeatureInteracted]) {
      setShowedPaywall(true)
      onOpenModalPremium(eventMap[lastFeatureInteracted], true)
    }
  }, [
    onGetChannelSettings,
    onOpenModalPremium,
    isDemo,
    hasLyrics,
    hasSections,
    isPremium,
    showedPaywall
  ])

  return {
    dispatchEventPaywall
  }
}
